import React from 'react';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';

import media from 'lib/media/media';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ClickAwayDetectableView from 'lib/ui/click-away-detectable-view';

import arrow from 'four-nets/icons/svg/icon_arrow-down.svg';
import search from 'four-nets/icons/svg/icon_search.svg';
import times from 'four-nets/icons/svg/icon_close.svg';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';

import FilterStackView from './filter-stack-view';
import defaultStyles from './filter-view.scss';
import searchiconStyles from './filter-view-searchicon.scss';

export interface Props {
    fields: React.ReactNode[];
    activeFilterFields: string | React.ReactChild[];
    fulltextValue?: string;
    title: React.ReactChild;
    displayText?: boolean;
    hideSearch?: boolean;
    styles: typeof defaultStyles;

    confirm: () => void;
    clear: () => void;
    onFulltextChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface State {
    open: boolean;
    isMobile: boolean;
}

@ClickAwayDetectableView({ display: 'inline-block' })
class FilterView extends React.Component<Props, State> {
    static defaultProps = {
        fields: [],
        activeFilterFields: '',
        fulltextValue: '',
    };

    mediaQueryListener?: MediaQueryList;
    stackViewRef: any;

    constructor(props: Props) {
        super(props);
        this.state = { open: false, isMobile: false };
    }

    setStackView = (ref: any) => {
        this.stackViewRef = ref;
    };

    onClickAway = (options: { event: MouseEvent }) => {
        if (this.state.open && !this.state.isMobile) {
            this.toggleOpen();
        }
    };

    toggleOpen = () => {
        this.setState(
            prevState => {
                return { open: !prevState.open };
            },
            () => {
                if (!this.state.open) {
                    this.props.confirm();
                }
            }
        );

        return Promise.resolve();
    };

    onClose = () => {
        this.setState({ open: false }, () => {
            this.props.confirm();
        });

        return Promise.resolve();
    };

    deselectAll = () => {
        this.props.clear();
    };

    componentDidMount() {
        this.mediaQueryListener = window.matchMedia(media.md);
        this.mediaQueryListener.addListener(this.handleResize);
        this.handleResize(this.mediaQueryListener);
    }

    componentWillUnmount() {
        if (this.mediaQueryListener) {
            this.mediaQueryListener.removeListener(this.handleResize);
        }
    }

    handleResize = (event: any) => {
        this.setState({ isMobile: !event.matches });
    };

    render() {
        const { styles, hideSearch, activeFilterFields } = this.props;

        const innerContentStyles = [styles.innerContent];
        if (this.state.open) {
            innerContentStyles.push(styles.open);
        }

        const deselectButtonStyles = [styles.deselect];
        if (activeFilterFields && !this.state.open) {
            deselectButtonStyles.push(styles.open);
        }

        const wrapperStyles = [styles.wrapper];
        if (activeFilterFields && !this.state.open) {
            wrapperStyles.push(styles.hasDeselect);
        }

        const buttonStyles = [styles.button];
        if (this.state.open) {
            buttonStyles.push(styles.buttonOpen);
        }

        const innerContent = (
            <div className={innerContentStyles.join(' ')}>
                <span className={styles.confirmButtonWrapper}>
                    <ActionButtonView onClick={this.toggleOpen}>
                        <FormattedMessage id='BAZAAR_CONFIRM_FILTER' />
                    </ActionButtonView>
                </span>
                {hideSearch ? null : (
                    <div className={styles.search}>
                        <SVGIcon style={{ image: searchiconStyles.searchIcon }} src={search} />
                        <FormattedMessage id='GO_SEARCH'>
                            {placeholder => (
                                <input
                                    type='text'
                                    className={styles.input}
                                    value={this.props.fulltextValue}
                                    onChange={this.props.onFulltextChange}
                                    placeholder={placeholder as string}
                                />
                            )}
                        </FormattedMessage>
                    </div>
                )}
                <div className={styles.container}>
                    {this.props.fields}
                    {this.props.displayText ? (
                        <span className={styles.noFilters}>
                            <FormattedMessage id='BAZAAR_NO_FILTER_MATCH' />
                        </span>
                    ) : null}
                </div>
            </div>
        );

        return (
            <div className={wrapperStyles.join(' ')}>
                <button onClick={this.deselectAll} className={deselectButtonStyles.join(' ')}>
                    <SVGIcon style={{ image: styles.icon }} src={times} />
                </button>
                <button onClick={this.toggleOpen} className={buttonStyles.join(' ')}>
                    <span className={styles.text}>{!activeFilterFields ? this.props.title : activeFilterFields}</span>
                    <SVGIcon style={{ image: styles.icon }} src={arrow} />
                </button>
                {this.state.isMobile ? (
                    <div className={styles.onlyMobile}>
                        <FilterStackView
                            open={this.state.open}
                            title={this.props.title}
                            innerRef={this.setStackView}
                            onPop={this.onClose}
                        >
                            {innerContent}
                        </FilterStackView>
                    </div>
                ) : (
                    <div className={styles.onlyDesktop}>{innerContent}</div>
                )}
            </div>
        );
    }
}

export default Styled(defaultStyles)(FilterView);
